import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../App';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [showBoot, setShowBoot] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    if (token) {
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Fetch user info
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Token invalid, clear it
      localStorage.removeItem('auth_token');
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData } = response.data;
      
      // Store token
      localStorage.setItem('auth_token', access_token);
      setToken(access_token);
      
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Show boot sequence instead of immediately setting user
      setPendingUser(userData);
      setShowBoot(true);
      
      return true;
    } catch (error) {
      toast.error('ACCESS_DENIED: Identifiants invalides');
      return false;
    }
  };

  const completeBoot = () => {
    setShowBoot(false);
    setUser(pendingUser);
    setPendingUser(null);
    toast.success(`SYSTÈME PRÊT - Bienvenue ${pendingUser?.full_name || 'OPÉRATEUR'}`);
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    toast.success('SESSION_TERMINATED - Déconnexion sécurisée');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      loading, 
      isAuthenticated: !!user,
      showBoot,
      pendingUser,
      completeBoot 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
